/** EOD chart math — ported from pages/idx_chart.php */

export function emaSafe(arr, period) {
  const out = new Array(arr.length).fill(null);
  if (!arr?.length) return out;
  const k = 2 / (period + 1);
  let seed = -1;
  for (let i = period - 1; i < arr.length; i++) {
    let ok = true;
    for (let j = i - (period - 1); j <= i; j++) {
      if (arr[j] == null || !isFinite(arr[j])) { ok = false; break; }
    }
    if (ok) { seed = i; break; }
  }
  if (seed < 0) return out;
  let sum = 0;
  for (let j = seed - (period - 1); j <= seed; j++) sum += arr[j];
  let prev = sum / period;
  out[seed] = +prev.toFixed(2);
  for (let i = seed + 1; i < arr.length; i++) {
    const v = arr[i];
    if (v == null || !isFinite(v)) { out[i] = null; continue; }
    prev = v * k + prev * (1 - k);
    out[i] = +prev.toFixed(2);
  }
  return out;
}

export function rma(vals, period) {
  const out = new Array(vals.length).fill(null);
  if (!vals.length) return out;
  let cnt = 0, sum = 0, seed = -1;
  for (let i = 0; i < vals.length; i++) {
    if (vals[i] == null) continue;
    sum += vals[i]; cnt++;
    if (cnt === period) { seed = i; break; }
  }
  if (seed < 0) return out;
  let prev = sum / period;
  out[seed] = prev;
  for (let i = seed + 1; i < vals.length; i++) {
    const v = vals[i];
    if (v == null) { out[i] = null; continue; }
    prev = (prev * (period - 1) + v) / period;
    out[i] = prev;
  }
  return out.map(v => (v != null ? +v.toFixed(2) : null));
}

function trueRange(H, L, C) {
  const tr = new Array(C.length).fill(null);
  for (let i = 0; i < C.length; i++) {
    if (H[i] == null || L[i] == null || C[i] == null) continue;
    if (i === 0 || C[i - 1] == null) tr[i] = H[i] - L[i];
    else {
      tr[i] = Math.max(H[i] - L[i], Math.abs(H[i] - C[i - 1]), Math.abs(L[i] - C[i - 1]));
    }
  }
  return tr;
}

export function atrWilder(H, L, C, period) {
  return rma(trueRange(H, L, C), period);
}

export function atrTrailingStop(H, L, C, period, mult) {
  const a = atrWilder(H, L, C, period);
  const stop = a.map(v => (v == null ? null : mult * v));
  const tsl = new Array(C.length).fill(null);
  let first = -1;
  for (let i = 0; i < C.length; i++) {
    if (a[i] != null && C[i] != null) { first = i; break; }
  }
  if (first < 0) return tsl;
  tsl[first] = C[first] - stop[first];
  for (let i = first + 1; i < C.length; i++) {
    if (C[i] == null || stop[i] == null) continue;
    const prevStop = tsl[i - 1];
    const close = C[i];
    const prevClose = C[i - 1];
    if (prevStop == null || prevClose == null) tsl[i] = close - stop[i];
    else if (close > prevStop && prevClose > prevStop) tsl[i] = Math.max(prevStop, close - stop[i]);
    else if (close < prevStop && prevClose < prevStop) tsl[i] = Math.min(prevStop, close + stop[i]);
    else tsl[i] = close > prevStop ? close - stop[i] : close + stop[i];
  }
  return tsl.map(v => (v != null ? +v.toFixed(2) : null));
}

export function linRegChannel(closes) {
  const vals = [];
  for (let i = 0; i < closes.length; i++) {
    if (closes[i] != null && closes[i] > 0) vals.push(closes[i]);
  }
  if (vals.length < 2) {
    return { lr: closes.map(() => null), ub: closes.map(() => null), lb: closes.map(() => null) };
  }
  const n = vals.length;
  let sx = 0, sy = 0, sxy = 0, sx2 = 0;
  for (let i = 0; i < n; i++) { sx += i; sy += vals[i]; sxy += i * vals[i]; sx2 += i * i; }
  const denom = n * sx2 - sx * sx;
  const slope = (n * sxy - sx * sy) / denom;
  const intercept = (sy - slope * sx) / n;
  const pred = [];
  for (let i = 0; i < n; i++) pred.push(slope * i + intercept);
  const mean = sy / n;
  let sumSq = 0;
  for (let i = 0; i < n; i++) sumSq += (vals[i] - mean) ** 2;
  const stdDev = Math.sqrt(sumSq / n);
  const lr = new Array(closes.length).fill(null);
  const ub = new Array(closes.length).fill(null);
  const lb = new Array(closes.length).fill(null);
  let vi = 0;
  for (let i = 0; i < closes.length; i++) {
    if (closes[i] != null && closes[i] > 0 && vi < n) {
      lr[i] = +pred[vi].toFixed(2);
      ub[i] = +(pred[vi] + stdDev).toFixed(2);
      lb[i] = +(pred[vi] - stdDev).toFixed(2);
      vi++;
    }
  }
  return { lr, ub, lb };
}

export function isoWeekKey(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  const day = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - day + 3);
  const firstThu = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  const firstDay = (firstThu.getUTCDay() + 6) % 7;
  firstThu.setUTCDate(firstThu.getUTCDate() - firstDay + 3);
  const week = 1 + Math.round((d - firstThu) / (7 * 24 * 3600 * 1000));
  return `${d.getUTCFullYear()}-W${String(week).padStart(2, '0')}`;
}

export const BOARD_TF_OPTIONS = [
  { value: 'D', label: 'Daily', eod: true },
  { value: 'W', label: 'Weekly', eod: true },
  { value: 'M', label: 'Monthly', eod: true },
  { value: '1m', label: '1 min', minutes: 1 },
  { value: '2m', label: '2 min', minutes: 2 },
  { value: '3m', label: '3 min', minutes: 3 },
  { value: '5m', label: '5 min', minutes: 5 },
  { value: '10m', label: '10 min', minutes: 10 },
  { value: '15m', label: '15 min', minutes: 15 },
  { value: '30m', label: '30 min', minutes: 30 },
  { value: '1h', label: '1 hour', minutes: 60 },
  { value: '2h', label: '2 hour', minutes: 120 },
  { value: '3h', label: '3 hour', minutes: 180 },
];

export function isIntradayTf(tf) {
  return tf !== 'D' && tf !== 'W' && tf !== 'M';
}

export function intradayIntervalMinutes(tf) {
  const opt = BOARD_TF_OPTIONS.find(o => o.value === tf);
  return opt?.minutes || 5;
}

/** Base candle resolution to fetch from API (1m/2m/5m from DB; 3m from Upstox). */
export function intradayFetchMinutes(tf) {
  const m = intradayIntervalMinutes(tf);
  if (m <= 1) return 1;
  if (m <= 2) return 2;
  if (m < 5) return 3;
  return 5;
}

/**
 * Parse api_ohlc_live.php → candle rows (unix time for lightweight-charts).
 * Prefers absolute `candles[]` series (multi-day backfill); falls back to labels/o/h/l/c.
 */
/** Drop bars whose IST open is outside NSE regular session (defence for dirty history). */
function isIstSessionOpenUnix(unixSec) {
  if (!unixSec || !Number.isFinite(unixSec)) return false;
  // Chart times are IST wall exposed as UTC getters (+19800 offset convention).
  const d = new Date(unixSec * 1000);
  const mins = d.getUTCHours() * 60 + d.getUTCMinutes();
  return mins >= (9 * 60 + 15) && mins < (15 * 60 + 30);
}

export function parseOhlcLiveResponse(data, tradeDate) {
  // Preferred: absolute series from multi-day / modern API path
  if (Array.isArray(data?.candles) && data.candles.length) {
    return data.candles
      .filter((c) => c && (c.close != null || c.c != null) && (c.time != null || c.date != null))
      .map((c) => ({
        date: Number(c.time ?? c.date),
        open: Number(c.open ?? c.o),
        high: Number(c.high ?? c.h),
        low: Number(c.low ?? c.l),
        close: Number(c.close ?? c.c),
        volume: Number(c.volume ?? c.v ?? 0),
      }))
      .filter((c) => c.date > 0 && Number.isFinite(c.close) && isIstSessionOpenUnix(c.date));
  }

  const candles = [];
  const labels = data?.labels || [];
  const day = data?.date || tradeDate;
  const IST_OFFSET = 19800;
  for (let i = 0; i < labels.length; i++) {
    if (data.o?.[i] == null || data.c?.[i] == null) continue;
    const lbl = String(labels[i] || '');
    const lm = lbl.match(/^(\d{1,2}):(\d{2})/);
    if (lm) {
      const mins = Number(lm[1]) * 60 + Number(lm[2]);
      if (mins < (9 * 60 + 15) || mins >= (15 * 60 + 30)) continue;
    }
    let unixSec = 0;
    try {
      const ts = new Date(`${day}T${labels[i]}:00+05:30`);
      unixSec = isNaN(ts) ? 0 : Math.floor(ts.getTime() / 1000) + IST_OFFSET;
    } catch (_) {}
    if (!unixSec) continue;
    candles.push({
      date: unixSec,
      open: Number(data.o[i]),
      high: Number(data.h?.[i] ?? Math.max(data.o[i], data.c[i])),
      low: Number(data.l?.[i] ?? Math.min(data.o[i], data.c[i])),
      close: Number(data.c[i]),
      volume: Number(data.volume?.[i] ?? 0),
    });
  }
  return candles;
}

/** How many calendar days of history to request for a given fetch interval. */
export function intradayHistoryDays(intervalMinutes) {
  if (intervalMinutes <= 1) return 5;   // ~5 sessions of 1m ≈ 1800+ bars
  if (intervalMinutes <= 2) return 10;
  if (intervalMinutes <= 5) return 15;
  return 20;
}

/** Aggregate intraday candles into larger bars (e.g. 3m → 10m / 15m / …). */
export function aggregateIntradayOHLC(candles, intervalMinutes, baseMinutes = 5) {
  if (!candles?.length) return [];
  if (intervalMinutes <= baseMinutes) return candles.slice();
  const bucketSec = intervalMinutes * 60;
  const buckets = new Map();
  for (const c of candles) {
    const t = typeof c.date === 'number' ? c.date : 0;
    if (!t) continue;
    const bucket = Math.floor(t / bucketSec) * bucketSec;
    if (!buckets.has(bucket)) {
      buckets.set(bucket, {
        date: bucket,
        open: c.open,
        high: c.high,
        low: c.low,
        close: c.close,
        volume: c.volume || 0,
      });
    } else {
      const b = buckets.get(bucket);
      b.high = Math.max(b.high, c.high);
      b.low = Math.min(b.low, c.low);
      b.close = c.close;
      b.volume += c.volume || 0;
    }
  }
  return Array.from(buckets.values()).sort((a, b) => a.date - b.date);
}

export function aggregateOHLCV(daily, tf) {
  if (tf === 'D') return daily.slice();
  const out = [];
  let cur = null, curKey = '';
  for (const r of daily) {
    if (!r?.date) continue;
    const k = tf === 'W' ? isoWeekKey(r.date) : r.date.slice(0, 7);
    if (!cur || k !== curKey) {
      if (cur) out.push(cur);
      curKey = k;
      cur = {
        date: r.date, open: r.open, high: r.high, low: r.low, close: r.close,
        volume: r.volume || 0, pe: r.pe ?? null, pb: r.pb ?? null, divYield: r.divYield ?? null,
      };
    } else {
      cur.high = Math.max(cur.high, r.high);
      cur.low = Math.min(cur.low, r.low);
      cur.close = r.close;
      cur.volume = (cur.volume || 0) + (r.volume || 0);
    }
  }
  if (cur) out.push(cur);
  return out;
}

export function computeFibLevels(H, L, n) {
  let hi = -Infinity, lo = Infinity;
  for (let i = 0; i < n; i++) {
    if (H[i] == null || L[i] == null) continue;
    if (H[i] > hi) hi = H[i];
    if (L[i] < lo) lo = L[i];
  }
  if (!isFinite(hi) || !isFinite(lo) || hi === lo) return null;
  const diff = hi - lo;
  const levels = [
    { k: '0%', price: hi },
    { k: '23.6%', price: hi - diff * 0.236 },
    { k: '38.2%', price: hi - diff * 0.382 },
    { k: '50%', price: hi - diff * 0.5 },
    { k: '61.8%', price: hi - diff * 0.618 },
    { k: '78.6%', price: hi - diff * 0.786 },
    { k: '100%', price: lo },
  ];
  levels.forEach(o => { o.price = +o.price.toFixed(2); });
  return { hi: +hi.toFixed(2), lo: +lo.toFixed(2), levels };
}

/** Session H/L fib ratios — matches api_ohlc_live.php / live OC mini charts. */
export const SESSION_FIB_RATIOS = [
  [-1.618, 'S3'], [-0.618, 'S2'], [-0.382, 'S1'],
  [0, '0'], [0.382, '0.382'], [0.5, '0.5'], [0.618, '0.618'],
  [1, '1'], [1.382, 'R1'], [1.618, 'R2'], [2.618, 'R3'],
];

export const SESSION_FIB_COLORS = {
  S3: '#ef5350', S2: '#ff7043', S1: '#ff8a65', 0: '#78909c',
  '0.382': '#4dd0e1', '0.5': '#26c6da', '0.618': '#00bcd4',
  1: '#ffd54f', R1: '#81c784', R2: '#66bb6a', R3: '#4caf50',
};

/** Fib from session high/low: price = lo + (hi - lo) * ratio. */
export function computeSessionFibLevels(candles) {
  if (!candles?.length) return null;
  let hi = -Infinity;
  let lo = Infinity;
  for (const c of candles) {
    if (c.high == null || c.low == null) continue;
    if (c.high > hi) hi = c.high;
    if (c.low < lo) lo = c.low;
  }
  if (!isFinite(hi) || !isFinite(lo)) return null;
  const rng = Math.max(hi - lo, 1);
  const levels = SESSION_FIB_RATIOS.map(([ratio, key]) => ({
    key,
    price: +(lo + rng * ratio).toFixed(2),
  }));
  return { hi: +hi.toFixed(2), lo: +lo.toFixed(2), levels };
}

/* ─── NSE session-aware Auto Fib (index board / intraday charts) ───────────
 * Chart timestamps are IST-as-UTC (real unix + 19800). UTC getters on those
 * values therefore return IST wall-clock components — do NOT re-apply TZ.
 *
 * Session window: 09:15 – 15:25 IST (15:25 = last 5m bar boundary).
 *
 * Auto Fib source (intraday):
 *   High & Low of the **first completed candle** of the selected timeframe
 *   for each trading day. Example: 5m → first 5-minute bar (usually 09:15);
 *   15m → first 15-minute bar. Fib is withheld until that bar is complete.
 * Lines still span the session (09:15–15:25) and never spill into the next day.
 */

/** NSE cash/index session minutes (IST). */
export const NSE_SESSION = {
  START_MIN: 9 * 60 + 15,  // 09:15
  END_MIN: 15 * 60 + 25,   // 15:25
};

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Standard daily H/L Fibonacci retracements.
 * 0% = day high, 100% = day low (classic pullback ladder).
 */
export const AUTO_FIB_RETRACEMENTS = [
  { k: '0%', ratio: 0, color: 'rgba(41,182,246,0.90)' },
  { k: '23.6%', ratio: 0.236, color: 'rgba(255,152,0,0.90)' },
  { k: '38.2%', ratio: 0.382, color: 'rgba(171,71,188,0.90)' },
  { k: '50%', ratio: 0.5, color: 'rgba(255,213,79,0.95)' },
  { k: '61.8%', ratio: 0.618, color: 'rgba(76,175,80,0.90)' },
  { k: '78.6%', ratio: 0.786, color: 'rgba(239,83,80,0.90)' },
  { k: '100%', ratio: 1, color: 'rgba(120,144,156,0.90)' },
];

export const AUTO_FIB_COLORS = Object.fromEntries(
  AUTO_FIB_RETRACEMENTS.map((lv) => [lv.k, lv.color]),
);

/** IST wall-clock parts from a chart (IST-spoofed) unix timestamp. */
export function chartTimeParts(unixSec) {
  const d = new Date(Number(unixSec) * 1000);
  return {
    y: d.getUTCFullYear(),
    m: d.getUTCMonth() + 1,
    day: d.getUTCDate(),
    h: d.getUTCHours(),
    min: d.getUTCMinutes(),
    sec: d.getUTCSeconds(),
  };
}

/** Minutes since midnight IST for a chart timestamp. */
export function chartMinutesOfDay(unixSec) {
  const { h, min } = chartTimeParts(unixSec);
  return h * 60 + min;
}

/** Trading-day key `YYYY-MM-DD` (IST) from a chart timestamp. */
export function sessionDayKey(unixSec) {
  const { y, m, day } = chartTimeParts(unixSec);
  return `${y}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** True when bar time is inside 09:15–15:25 IST (inclusive). */
export function isNseSessionBar(unixSec) {
  const mins = chartMinutesOfDay(unixSec);
  return mins >= NSE_SESSION.START_MIN && mins <= NSE_SESSION.END_MIN;
}

/**
 * Session open/close as chart-unix (IST-spoofed) for a day key.
 * Matches the board's candle time base so LineSeries points align.
 */
export function sessionBoundsUnix(dayKey) {
  const [y, m, d] = String(dayKey).split('-').map(Number);
  if (!y || !m || !d) return null;
  return {
    start: Math.floor(Date.UTC(y, m - 1, d, 9, 15, 0) / 1000),
    end: Math.floor(Date.UTC(y, m - 1, d, 15, 25, 0) / 1000),
  };
}

/**
 * Format chart timestamp for X-axis / crosshair.
 * Example: `14 Jul 14:30`
 */
export function formatChartDateTime(unixSec, { dateOnly = false, timeOnly = false } = {}) {
  if (typeof unixSec !== 'number' || !isFinite(unixSec)) return String(unixSec ?? '');
  const { day, m, h, min } = chartTimeParts(unixSec);
  const mon = MONTHS_SHORT[m - 1] || '—';
  const hh = String(h).padStart(2, '0');
  const mm = String(min).padStart(2, '0');
  if (dateOnly) return `${day} ${mon}`;
  if (timeOnly) return `${hh}:${mm}`;
  return `${day} ${mon} ${hh}:${mm}`;
}

/**
 * True when this bar is the NSE session open (09:15 IST), or the first in-session
 * bar of its trading day (for coarser TFs where the open bar is not exactly 09:15).
 *
 * @param {number} unixSec - chart (IST-spoofed) unix time
 * @param {Set<number>|null} [sessionOpenTimes] - optional set of first-bar times per day
 */
export function isNseSessionOpenTick(unixSec, sessionOpenTimes = null) {
  if (typeof unixSec !== 'number' || !isFinite(unixSec)) return false;
  if (sessionOpenTimes && sessionOpenTimes.has(unixSec)) return true;
  return chartMinutesOfDay(unixSec) === NSE_SESSION.START_MIN;
}

/**
 * First in-session bar per trading day — used for axis date labels + tick formatter.
 * One mark per day; time is the actual first session bar (usually 09:15).
 *
 * @param {Array<{date?: number, time?: number}>} candles
 * @returns {Array<{ dayKey: string, time: number, label: string, shortLabel: string }>}
 */
export function collectSessionOpenAxisMarks(candles) {
  if (!candles?.length) return [];
  const seen = new Set();
  const out = [];
  for (const c of candles) {
    const t = typeof c.date === 'number' ? c.date : (typeof c.time === 'number' ? c.time : null);
    if (t == null || !isNseSessionBar(t)) continue;
    const key = sessionDayKey(t);
    if (seen.has(key)) continue;
    seen.add(key);
    const { day, m, h, min } = chartTimeParts(t);
    const mon = MONTHS_SHORT[m - 1] || '—';
    const hh = String(h).padStart(2, '0');
    const mm = String(min).padStart(2, '0');
    const atExactOpen = (h * 60 + min) === NSE_SESSION.START_MIN;
    out.push({
      dayKey: key,
      time: t,
      // Prefer "15 Jul 09:15" at exact open; otherwise short date for late first bars
      label: atExactOpen ? `${day} ${mon} ${hh}:${mm}` : `${day} ${mon}`,
      shortLabel: `${day} ${mon}`,
    });
  }
  return out;
}

/** Build a Set of session-open bar times for O(1) tick checks. */
export function sessionOpenTimeSet(candles) {
  const marks = collectSessionOpenAxisMarks(candles);
  return new Set(marks.map((m) => m.time));
}

/**
 * Smart X-axis tick labels (TradingView-style, NSE sessions).
 *
 * Rules:
 * 1. Normal time ticks  → `HH:mm` only  (e.g. `10:00`, `11:00`)
 * 2. Session open only  → `D Mon` or `D Mon HH:mm`  (e.g. `15 Jul` / `15 Jul 09:15`)
 *    — once per trading day, at 09:15 or the day's first in-session bar
 *    (disable with `sessionDateOnTick: false` when a DOM overlay owns dates)
 * 3. DayOfMonth marks   → `D Mon` (LWC major day ticks; bold when allowBoldLabels)
 * 4. Month / Year       → hierarchical scale labels
 *
 * Pass `sessionOpenTimes` (Set of first-bar unix times) so coarser TFs still get
 * a single date label on the first bar of each session, not only exact 09:15.
 *
 * Note: keep tickMarkMaxCharacterLength ≥ 12 so `15 Jul 09:15` is not clipped
 * (LWC default is 8, which truncates date+time labels).
 */
export function formatChartTickMark(unixSec, tickMarkType, opts = {}) {
  // TickMarkType: Year=0, Month=1, DayOfMonth=2, Time=3, TimeWithSeconds=4
  if (typeof unixSec !== 'number') return String(unixSec ?? '');
  const { y, day, m, h, min } = chartTimeParts(unixSec);
  const mon = MONTHS_SHORT[m - 1] || '—';
  const hh = String(h).padStart(2, '0');
  const mm = String(min).padStart(2, '0');
  const mins = h * 60 + min;

  if (tickMarkType === 0) return String(y);
  if (tickMarkType === 1) return `${mon} ${y}`;

  // When a DOM overlay owns session dates, keep every LWC tick as pure time
  // so the axis stays clean and dates appear once (below) at session open only.
  const dateOnTick = opts.sessionDateOnTick !== false;
  if (!dateOnTick) {
    return `${hh}:${mm}`;
  }

  // Major day marks from LWC (often land on the first bar after the overnight gap)
  if (tickMarkType === 2) return `${day} ${mon}`;

  // Session open → date once per trading day
  if (isNseSessionOpenTick(unixSec, opts.sessionOpenTimes || null)) {
    if (mins === NSE_SESSION.START_MIN) {
      return `${day} ${mon} ${hh}:${mm}`;
    }
    return `${day} ${mon}`;
  }

  // All other intraday ticks: time only — keeps multi-day axis clean
  return `${hh}:${mm}`;
}

/**
 * Daily High / Low for one session from OHLC bars (session filter applied).
 */
export function calcSessionHighLow(candles) {
  if (!candles?.length) return null;
  let hi = -Infinity;
  let lo = Infinity;
  let firstTime = null;
  let lastTime = null;
  for (const c of candles) {
    if (c == null || c.high == null || c.low == null) continue;
    const t = typeof c.date === 'number' ? c.date : (typeof c.time === 'number' ? c.time : null);
    if (t == null) continue;
    if (!isNseSessionBar(t)) continue;
    if (c.high > hi) hi = c.high;
    if (c.low < lo) lo = c.low;
    if (firstTime == null || t < firstTime) firstTime = t;
    if (lastTime == null || t > lastTime) lastTime = t;
  }
  if (!isFinite(hi) || !isFinite(lo) || firstTime == null) return null;
  return { hi, lo, firstTime, lastTime };
}

/**
 * Build classic Fib retracement prices from a day high/low.
 * price = hi - (hi - lo) * ratio  →  0% at high, 100% at low.
 */
export function fibLevelsFromHighLow(hi, lo) {
  if (!isFinite(hi) || !isFinite(lo) || hi === lo) return null;
  const diff = hi - lo;
  return AUTO_FIB_RETRACEMENTS.map((lv) => ({
    k: lv.k,
    key: lv.k,
    ratio: lv.ratio,
    color: lv.color,
    price: +(hi - diff * lv.ratio).toFixed(2),
  }));
}

/**
 * Chart-time "now" (IST-spoofed unix) used by fib completion checks.
 * @param {number} [nowUnix]
 */
export function chartNowUnix(nowUnix) {
  if (typeof nowUnix === 'number' && isFinite(nowUnix)) return nowUnix;
  // Candles use IST-as-UTC timestamps (real epoch + 19800)
  return Math.floor(Date.now() / 1000) + 19800;
}

/**
 * Whether a TF bar starting at `barTime` has fully completed.
 * Complete when wall-clock is past bar end, or a later bar already exists.
 *
 * @param {number} barTime - candle open (chart unix)
 * @param {number} intervalMinutes - selected timeframe size
 * @param {{ nowUnix?: number, hasNextBar?: boolean }} [opts]
 */
export function isTfCandleCompleted(barTime, intervalMinutes, opts = {}) {
  if (typeof barTime !== 'number' || !isFinite(barTime)) return false;
  const mins = Math.max(1, Number(intervalMinutes) || 5);
  if (opts.hasNextBar) return true;
  const barEnd = barTime + mins * 60;
  return chartNowUnix(opts.nowUnix) >= barEnd;
}

/**
 * First in-session candle of a trading day from an OHLC series.
 * @returns {{ time, open, high, low, close, volume }|null}
 */
export function firstSessionCandle(candles, dayKey = null) {
  if (!candles?.length) return null;
  let best = null;
  for (const c of candles) {
    if (c == null || c.high == null || c.low == null) continue;
    const t = typeof c.date === 'number' ? c.date : (typeof c.time === 'number' ? c.time : null);
    if (t == null || !isNseSessionBar(t)) continue;
    if (dayKey && sessionDayKey(t) !== dayKey) continue;
    if (!best || t < best.time) {
      best = {
        time: t,
        open: Number(c.open),
        high: Number(c.high),
        low: Number(c.low),
        close: Number(c.close),
        volume: Number(c.volume || 0),
      };
    }
  }
  return best;
}

/**
 * Group OHLC into NSE session buckets and compute session-limited Auto Fib
 * from the **first completed candle** of the selected timeframe.
 *
 * @param {Array} candles - `{ date|time, high, low, ... }` with IST-spoofed unix times
 * @param {{ nowUnix?: number, intervalMinutes?: number }} [opts]
 * @returns {Array<{ dayKey, hi, lo, startTime, endTime, frozen, levels, sourceTime, intervalMinutes, userAdjusted?: boolean }>}
 *
 * Rules:
 * - Per day: H/L come from the first in-session bar of the TF (not the full-day range).
 * - Fib is drawn only after that first bar is fully completed.
 * - startTime is always session open 09:15 (new Fib begins at open).
 * - Completed days: endTime fixed at 15:25 (no spill into next day).
 * - Active day: endTime tracks the latest in-session bar; freezes at 15:25 once reached.
 * - Changing TF recalculates because first-bar H/L and completion depend on interval.
 */
export function computeSessionScopedAutoFib(candles, opts = {}) {
  if (!candles?.length) return [];

  const intervalMinutes = Math.max(1, Number(opts.intervalMinutes) || 5);
  const nowUnix = chartNowUnix(opts.nowUnix);

  /** @type {Map<string, { dayKey: string, first: object, lastTime: number, hasBarAfterFirst: boolean }>} */
  const byDay = new Map();
  for (const c of candles) {
    if (c == null || c.high == null || c.low == null) continue;
    const t = typeof c.date === 'number' ? c.date : (typeof c.time === 'number' ? c.time : null);
    if (t == null || !isNseSessionBar(t)) continue;
    const key = sessionDayKey(t);
    let bucket = byDay.get(key);
    if (!bucket) {
      bucket = {
        dayKey: key,
        first: {
          time: t,
          open: Number(c.open),
          high: Number(c.high),
          low: Number(c.low),
          close: Number(c.close),
          volume: Number(c.volume || 0),
        },
        lastTime: t,
        hasBarAfterFirst: false,
      };
      byDay.set(key, bucket);
      continue;
    }
    if (t < bucket.first.time) {
      // Earlier bar becomes the first-candle source; previous first is "after"
      bucket.hasBarAfterFirst = true;
      bucket.first = {
        time: t,
        open: Number(c.open),
        high: Number(c.high),
        low: Number(c.low),
        close: Number(c.close),
        volume: Number(c.volume || 0),
      };
    } else if (t > bucket.first.time) {
      bucket.hasBarAfterFirst = true;
    }
    if (t > bucket.lastTime) bucket.lastTime = t;
  }

  if (!byDay.size) return [];

  const dayKeys = Array.from(byDay.keys()).sort();
  const latestKey = dayKeys[dayKeys.length - 1];

  const out = [];
  for (const dayKey of dayKeys) {
    const b = byDay.get(dayKey);
    const first = b.first;
    if (!first || !isFinite(first.high) || !isFinite(first.low)) continue;

    // Wait until the first TF candle is fully completed
    if (!isTfCandleCompleted(first.time, intervalMinutes, {
      nowUnix,
      hasNextBar: b.hasBarAfterFirst,
    })) {
      continue;
    }

    const hi = +Number(first.high).toFixed(2);
    const lo = +Number(first.low).toFixed(2);
    if (hi === lo) continue;

    const bounds = sessionBoundsUnix(dayKey);
    if (!bounds) continue;

    const pastCloseOnDay = sessionDayKey(nowUnix) > dayKey
      || (sessionDayKey(nowUnix) === dayKey && chartMinutesOfDay(nowUnix) >= NSE_SESSION.END_MIN);
    const barsReachedClose = chartMinutesOfDay(b.lastTime) >= NSE_SESSION.END_MIN;
    const frozen = dayKey !== latestKey || pastCloseOnDay || barsReachedClose;

    const startTime = bounds.start; // always 09:15 — fib segment spans the session
    // Active day: extend at least through the first completed TF bar so a single
    // completed open bar still produces a visible horizontal segment.
    const firstBarEnd = first.time + intervalMinutes * 60;
    const endTime = frozen
      ? bounds.end
      : Math.min(Math.max(b.lastTime, firstBarEnd, bounds.start + 60), bounds.end);

    if (endTime <= startTime) continue;

    const levels = fibLevelsFromHighLow(hi, lo);
    if (!levels) continue;

    out.push({
      dayKey,
      hi,
      lo,
      startTime,
      endTime,
      frozen,
      levels,
      sourceTime: first.time,
      intervalMinutes,
    });
  }

  return out;
}

/**
 * Apply a user high/low override to a fib session (live drag / stretch).
 * @param {object} session - from computeSessionScopedAutoFib
 * @param {number} hi
 * @param {number} lo
 */
export function applyUserFibHighLow(session, hi, lo) {
  if (!session) return null;
  const h = +Number(hi).toFixed(2);
  const l = +Number(lo).toFixed(2);
  if (!isFinite(h) || !isFinite(l) || h === l) return session;
  // Normalize so 0% is always the higher price
  const top = Math.max(h, l);
  const bot = Math.min(h, l);
  const levels = fibLevelsFromHighLow(top, bot);
  if (!levels) return session;
  return {
    ...session,
    hi: top,
    lo: bot,
    levels,
    userAdjusted: true,
  };
}

/** Compact signature for dirty-checking fib redraws (perf). */
export function sessionFibSignature(sessions) {
  if (!sessions?.length) return '';
  return sessions.map((s) => (
    `${s.dayKey}:${s.hi}:${s.lo}:${s.startTime}:${s.endTime}:${s.frozen ? 1 : 0}`
    + `:${s.sourceTime || 0}:${s.intervalMinutes || 0}:${s.userAdjusted ? 1 : 0}`
  )).join('|');
}

export function mapSeriesToVisible(fullDates, series, visibleDates) {
  const mp = new Map();
  for (let i = 0; i < fullDates.length; i++) mp.set(fullDates[i], series[i]);
  return visibleDates.map(d => (mp.has(d) ? mp.get(d) : null));
}

export function calcFromDate(period) {
  const now = new Date();
  const to = new Date(now);
  const from = new Date(now);
  switch (period) {
    case '5D': from.setDate(from.getDate() - 7); break;
    case '1W': from.setDate(from.getDate() - 10); break;
    case '1M': from.setMonth(from.getMonth() - 1); break;
    case '3M': from.setMonth(from.getMonth() - 3); break;
    case '6M': from.setMonth(from.getMonth() - 6); break;
    case '1Y': from.setFullYear(from.getFullYear() - 1); break;
    case '3Y': from.setFullYear(from.getFullYear() - 3); break;
    case '5Y': from.setFullYear(from.getFullYear() - 5); break;
    case 'ALL': return { from: '', to: '' };
    default: from.setMonth(from.getMonth() - 6);
  }
  const pad = n => String(n).padStart(2, '0');
  return {
    from: `${from.getFullYear()}-${pad(from.getMonth() + 1)}-${pad(from.getDate())}`,
    to: `${to.getFullYear()}-${pad(to.getMonth() + 1)}-${pad(to.getDate())}`,
  };
}

export function discoverFOReversalLevels(foCache, fullRaw) {
  if (!foCache?.length || !fullRaw?.length) return null;
  const priceMap = new Map();
  const priceDates = [];
  fullRaw.forEach(r => {
    priceMap.set(r.date, { close: +r.close, high: +r.high, low: +r.low });
    priceDates.push(r.date);
  });
  const foMap = new Map();
  foCache.forEach(r => foMap.set(r.date, r.foScore));
  const obStats = {}, osStats = {};
  for (let lv = 14; lv <= 20; lv++) obStats[lv] = { hits: 0, reversed: 0, totalMove: 0 };
  for (let lv = 0; lv <= 6; lv++) osStats[lv] = { hits: 0, reversed: 0, totalMove: 0 };

  const checkReversal = (p0, di, priceDates, priceMap, isOB) => {
    let sharp = false, cons = false, maxMove = 0;
    for (let f = 1; f <= 3 && di + f < priceDates.length; f++) {
      const pf = priceMap.get(priceDates[di + f]);
      if (!pf) continue;
      const chg = ((pf.close - p0.close) / p0.close) * 100;
      if (isOB) { if (chg < maxMove) maxMove = chg; if (chg <= -1) { sharp = true; break; } }
      else { if (chg > maxMove) maxMove = chg; if (chg >= 1) { sharp = true; break; } }
    }
    if (!sharp) {
      let consoDays = 0;
      for (let f = 1; f <= 5 && di + f < priceDates.length; f++) {
        const pf = priceMap.get(priceDates[di + f]);
        if (!pf) continue;
        if (Math.abs(((pf.close - p0.close) / p0.close) * 100) <= 0.5) consoDays++;
        else break;
      }
      if (consoDays >= 2) {
        const lookFrom = consoDays + 1;
        for (let f = lookFrom; f <= lookFrom + 3 && di + f < priceDates.length; f++) {
          const pf = priceMap.get(priceDates[di + f]);
          if (!pf) continue;
          const chg = ((pf.close - p0.close) / p0.close) * 100;
          if (isOB) { if (chg < maxMove) maxMove = chg; if (chg <= -1) { cons = true; break; } }
          else { if (chg > maxMove) maxMove = chg; if (chg >= 1) { cons = true; break; } }
        }
      }
    }
    return { reversed: sharp || cons, maxMove };
  };

  for (let di = 0; di < priceDates.length; di++) {
    const score = foMap.get(priceDates[di]);
    if (score == null || di + 5 >= priceDates.length) continue;
    const p0 = priceMap.get(priceDates[di]);
    if (!p0) continue;
    if (score >= 14 && score <= 20) {
      const stat = obStats[score]; stat.hits++;
      const r = checkReversal(p0, di, priceDates, priceMap, true);
      if (r.reversed) { stat.reversed++; stat.totalMove += Math.abs(r.maxMove); }
    }
    if (score >= 0 && score <= 6) {
      const stat = osStats[score]; stat.hits++;
      const r = checkReversal(p0, di, priceDates, priceMap, false);
      if (r.reversed) { stat.reversed++; stat.totalMove += r.maxMove; }
    }
  }

  let bestOB = null, bestOBs = 0, bestOS = null, bestOSs = 0;
  for (let lv = 14; lv <= 20; lv++) {
    const s = obStats[lv]; if (s.hits < 3) continue;
    const rate = s.reversed / s.hits;
    const avgMove = s.reversed > 0 ? s.totalMove / s.reversed : 0;
    const sc = rate * Math.min(s.hits, 20) / 20 * (1 + avgMove / 5);
    if (rate >= 0.4 && sc > bestOBs) { bestOBs = sc; bestOB = lv; }
  }
  for (let lv = 0; lv <= 6; lv++) {
    const s = osStats[lv]; if (s.hits < 3) continue;
    const rate = s.reversed / s.hits;
    const avgMove = s.reversed > 0 ? s.totalMove / s.reversed : 0;
    const sc = rate * Math.min(s.hits, 20) / 20 * (1 + avgMove / 5);
    if (rate >= 0.4 && sc > bestOSs) { bestOSs = sc; bestOS = lv; }
  }
  return { obLevel: bestOB ?? 17, osLevel: bestOS ?? 4 };
}

export function discoverEQReversalLevels(eqCache, fullRaw) {
  if (!eqCache?.data?.length || !fullRaw?.length) return null;
  const maxS = eqCache.maxScore || 20;
  const priceMap = new Map();
  const priceDates = [];
  fullRaw.forEach(r => {
    priceMap.set(r.date, { close: +r.close });
    priceDates.push(r.date);
  });
  const eqMap = new Map();
  eqCache.data.forEach(r => eqMap.set(r.date, r.eqScore));
  const obMin = Math.round(maxS * 0.7), osMax = Math.round(maxS * 0.3);
  const obStats = {}, osStats = {};
  for (let lv = obMin; lv <= maxS; lv++) obStats[lv] = { hits: 0, reversed: 0 };
  for (let lv = 0; lv <= osMax; lv++) osStats[lv] = { hits: 0, reversed: 0 };

  for (let di = 0; di < priceDates.length; di++) {
    const score = eqMap.get(priceDates[di]);
    if (score == null || di + 5 >= priceDates.length) continue;
    const p0 = priceMap.get(priceDates[di]);
    if (!p0) continue;
    if (score >= obMin) {
      const stat = obStats[score]; stat.hits++;
      for (let f = 1; f <= 3 && di + f < priceDates.length; f++) {
        const pf = priceMap.get(priceDates[di + f]);
        if (!pf) continue;
        if (((pf.close - p0.close) / p0.close) * 100 <= -1) { stat.reversed++; break; }
      }
    }
    if (score <= osMax) {
      const stat = osStats[score]; stat.hits++;
      for (let f = 1; f <= 3 && di + f < priceDates.length; f++) {
        const pf = priceMap.get(priceDates[di + f]);
        if (!pf) continue;
        if (((pf.close - p0.close) / p0.close) * 100 >= 1) { stat.reversed++; break; }
      }
    }
  }

  let bestOB = null, bestOBs = 0, bestOS = null, bestOSs = 0;
  for (let lv = obMin; lv <= maxS; lv++) {
    const s = obStats[lv]; if (s.hits < 3) continue;
    const rate = s.reversed / s.hits;
    const sc = rate * Math.min(s.hits, 20) / 20;
    if (rate >= 0.35 && sc > bestOBs) { bestOBs = sc; bestOB = lv; }
  }
  for (let lv = 0; lv <= osMax; lv++) {
    const s = osStats[lv]; if (s.hits < 3) continue;
    const rate = s.reversed / s.hits;
    const sc = rate * Math.min(s.hits, 20) / 20;
    if (rate >= 0.35 && sc > bestOSs) { bestOSs = sc; bestOS = lv; }
  }
  return {
    obLevel: bestOB ?? (maxS === 12 ? 10 : 17),
    osLevel: bestOS ?? (maxS === 12 ? 3 : 4),
    maxScore: maxS,
  };
}

export function buildCPRSegments(levels, dates) {
  if (!levels?.length || !dates?.length) return [];
  const segments = [];
  for (const lv of levels) {
    let fi = -1, li = -1;
    for (let i = 0; i < dates.length; i++) {
      const d = dates[i];
      if (d >= lv.from_date && d <= lv.to_date) {
        if (fi < 0) fi = i;
        li = i;
      }
    }
    if (fi < 0) continue;
    segments.push({
      fi, li, from: dates[fi], to: dates[li],
      pivot: lv.pivot, tc: lv.tc, bc: lv.bc,
      r1: lv.r1, s1: lv.s1, r2: lv.r2, s2: lv.s2, r3: lv.r3, s3: lv.s3,
      ph: lv.prev_high, pl: lv.prev_low,
    });
  }
  return segments;
}

export function fmtVol(n) {
  if (!n) return '—';
  const a = Math.abs(n);
  if (a >= 1e7) return `${(n / 1e7).toFixed(2)}Cr`;
  if (a >= 1e5) return `${(n / 1e5).toFixed(2)}L`;
  if (a >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return String(n);
}

/** Compact signed count for participant OI / day-change (0 stays 0). */
export function fmtPartQty(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  const v = Number(n);
  const sign = v > 0 ? '+' : v < 0 ? '-' : '';
  const a = Math.abs(v);
  if (a >= 1e7) return `${sign}${(a / 1e7).toFixed(2)}Cr`;
  if (a >= 1e5) return `${sign}${(a / 1e5).toFixed(2)}L`;
  if (a >= 1e3) return `${sign}${(a / 1e3).toFixed(1)}K`;
  return `${sign}${Math.round(a)}`;
}

export const PART_HOVER_ORDER = ['CLIENT', 'FII', 'PRO', 'DII'];

/**
 * Resolve participant series row for a candle (exact date, or last day in a W/M bar).
 * @param {Map<string, object>} partSeriesByDate
 * @param {Array<{date:string}>} viewData
 * @param {number} idx
 */
export function resolvePartRow(partSeriesByDate, viewData, idx) {
  if (!partSeriesByDate?.size || !viewData?.[idx]) return null;
  const barDate = viewData[idx].date;
  if (partSeriesByDate.has(barDate)) return partSeriesByDate.get(barDate);
  const end = viewData[idx + 1]?.date || '9999-99-99';
  let best = null;
  partSeriesByDate.forEach((row, d) => {
    if (d >= barDate && d < end) best = row;
  });
  return best;
}

/**
 * HTML chips for Client · FII · Pro · DII on hover.
 * compact=true → Δ only (in-chart legend); false → net + Δ (header strip).
 */
export function buildPartHoverHtml(row, { compact = false } = {}) {
  if (!row) return '';
  const bits = [];
  for (const ct of PART_HOVER_ORDER) {
    const p = row[ct];
    if (!p) continue;
    const dc = p.netDC;
    const net = p.net;
    const hasDc = dc != null && !Number.isNaN(Number(dc));
    const hasNet = net != null && !Number.isNaN(Number(net));
    if (!hasDc && !hasNet) continue;
    let col = 'var(--text-dim)';
    if (p.direction === 'bull' || (hasDc && dc > 0)) col = '#39FF14';
    else if (p.direction === 'bear' || (hasDc && dc < 0)) col = '#EC407A';
    const tag = p.fresh
      ? (p.direction === 'bull' ? (p.is3D ? ' ▲FL3' : ' ▲FL') : (p.is3D ? ' ▼FS3' : ' ▼FS'))
      : '';
    const title = [
      p.label || ct,
      hasNet ? `Net ${fmtPartQty(net)}` : null,
      hasDc ? `Δ ${fmtPartQty(dc)}` : null,
      p.daLong != null ? `ΔL ${fmtPartQty(p.daLong)}` : null,
      p.daShort != null ? `ΔS ${fmtPartQty(p.daShort)}` : null,
      p.fresh || null,
    ].filter(Boolean).join(' · ');
    const body = compact
      ? `${hasDc ? fmtPartQty(dc) : fmtPartQty(net)}${tag}`
      : `${hasNet ? fmtPartQty(net) : '—'} <span style="opacity:.85">(${hasDc ? fmtPartQty(dc) : '—'})</span>${tag}`;
    bits.push(
      `<span title="${title}"><span class="ol" style="color:#FFD54F">${p.short || ct}</span> <b style="color:${col}">${body}</b></span>`,
    );
  }
  return bits.join('');
}

export function fmtShortDate(d) {
  const dt = new Date(d + 'T00:00:00');
  return `${dt.getDate()} ${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][dt.getMonth()]}`;
}

export function fmtFullDate(d) {
  const dt = new Date(d + 'T00:00:00');
  return `${dt.getDate()} ${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][dt.getMonth()]} ${dt.getFullYear()}`;
}

/** Bar color for FO/EQ score histograms (matches EodScorePanel). */
export function scoreBarColor(v, i, scores, obLevel, osLevel) {
  if (v == null) return 'rgba(128,128,128,0.3)';
  const mid = Math.round((obLevel + osLevel) / 2);
  const p1 = i >= 1 ? scores[i - 1] : null;
  const p2 = i >= 2 ? scores[i - 2] : null;
  if (v >= obLevel && p1 != null && p1 >= obLevel && p2 != null && p2 >= obLevel) return 'rgba(255,0,255,0.90)';
  if (v >= obLevel) return 'rgba(236,64,122,0.85)';
  if (v <= osLevel && p1 != null && p1 <= osLevel && p2 != null && p2 <= osLevel) return 'rgba(0,255,65,0.90)';
  if (v <= osLevel) return 'rgba(57,255,20,0.80)';
  if (v > mid) return 'rgba(124,252,0,0.75)';
  return 'rgba(239,83,80,0.75)';
}

/** Vertical pane layout for candle + optional FO/EQ score strips (shared time axis). */
export function chartPaneMargins(showFo, showEq) {
  if (showFo && showEq) {
    return {
      candle: { top: 0.05, bottom: 0.42 },
      fo: { top: 0.62, bottom: 0.22 },
      eq: { top: 0.80, bottom: 0.02 },
    };
  }
  if (showFo) {
    return {
      candle: { top: 0.05, bottom: 0.22 },
      fo: { top: 0.82, bottom: 0.02 },
      eq: null,
    };
  }
  if (showEq) {
    return {
      candle: { top: 0.05, bottom: 0.22 },
      fo: null,
      eq: { top: 0.82, bottom: 0.02 },
    };
  }
  return {
    candle: { top: 0.05, bottom: 0.05 },
    fo: null,
    eq: null,
  };
}

export const DEFAULT_TOGGLES = {
  ema20: false, ema50: false, ema100: false, ema200: false,
  lrc: false, atr217: false, atr103: false,
  fib: false,
  cprM: false, cprW: false,
  monthLines: true,
  svwap: true, psvwap: true, foScore: true, eqScore: true,
  partSig: true,
};

export const SETTINGS_KEY = 'tmr_chart_settings';